import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  limit,
} from "firebase/firestore";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import ReactMarkdown from "react-markdown";

const HaberDetay = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageUrl, setImageUrl] = useState("/images/placeholder.jpg");
  const [similarProjects, setSimilarProjects] = useState([]);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const db = getFirestore();
        const storage = getStorage();

        const generalNewsRef = collection(db, "news");
        const docRef = doc(generalNewsRef, id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setNews(data);

          if (data.image_url) {
            try {
              const storageRef = ref(storage, data.image_url);
              const url = await getDownloadURL(storageRef);
              setImageUrl(url);
            } catch (error) {
              console.warn("Görsel yüklenemedi:", error.message);
              setImageUrl("/images/placeholder.jpg");
            }
          } else {
            setImageUrl("/images/placeholder.jpg");
          }
        } else {
          setNews(null);
        }
      } catch (error) {
        console.error("Haber alınırken hata:", error);
        setNews(null);
      } finally {
        setLoading(false);
      }
    };

    const fetchProjects = async () => {
      try {
        const db = getFirestore();
        const projectsRef = collection(db, "projects");
        const q = query(projectsRef, limit(4));
        const snapshot = await getDocs(q);
        const projects = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setSimilarProjects(projects);
      } catch (error) {
        console.error("Projeler yüklenirken hata:", error);
      }
    };

    fetchNews();
    fetchProjects();
  }, [id]);

  const formatPrice = (price) => {
    if (!price) return "Fiyat bilgisi yok";
    if (typeof price === "string") {
      if (price.includes("TL") || price.includes("₺")) return price;
      const numericPrice = price.replace(/[^\d]/g, "");
      if (numericPrice)
        return `${parseInt(numericPrice).toLocaleString("tr-TR")} TL`;
    }
    if (typeof price === "number")
      return `${price.toLocaleString("tr-TR")} TL`;
    return price;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner} />
        <p style={{ color: "#666", marginTop: "16px" }}>Yükleniyor...</p>
      </div>
    );
  }

  if (!news) {
    return (
      <div style={styles.error}>
        <p>Haber bulunamadı.</p>
      </div>
    );
  }

  return (
    <div style={styles.page}>

      {/* ── HERO BANNER ── */}
      <div style={styles.hero}>
        <img
          src={imageUrl}
          alt={news.title || "Haber Görseli"}
          style={styles.heroImage}
        />
        <div style={styles.heroOverlay} />
        <div style={styles.heroContent}>
          {news.category && (
            <span style={styles.categoryBadge}>{news.category}</span>
          )}
          <h1 style={styles.heroTitle}>{news.title}</h1>
          <div style={styles.hereMeta}>
            {news.author && (
              <span style={styles.metaItem}>
                <svg style={styles.metaIcon} viewBox="0 0 24 24" fill="none">
                  <path
                    d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <circle
                    cx="12"
                    cy="7"
                    r="4"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </svg>
                {news.author}
              </span>
            )}
            {(news.created_at || news.date) && (
              <span style={styles.metaItem}>
                <svg style={styles.metaIcon} viewBox="0 0 24 24" fill="none">
                  <rect
                    x="3"
                    y="4"
                    width="18"
                    height="18"
                    rx="2"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    d="M16 2v4M8 2v4M3 10h18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                {formatDate(news.created_at || news.date)}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── İÇERİK ── */}
      <div style={styles.contentWrapper}>

        {/* Özet / Lead paragraph */}
        {news.summary && (
          <p style={styles.leadText}>{news.summary}</p>
        )}

        <div style={styles.divider} />

        <ReactMarkdown
          children={news.content || ""}
          components={{
            p: ({ node, ...props }) => (
              <p style={styles.bodyText} {...props} />
            ),
            strong: ({ node, ...props }) => (
              <strong style={{ fontWeight: "700", color: "#1a1a1a" }} {...props} />
            ),
            h2: ({ node, ...props }) => (
              <h2 style={styles.inlineH2} {...props} />
            ),
            h3: ({ node, ...props }) => (
              <h3 style={styles.inlineH3} {...props} />
            ),
            blockquote: ({ node, ...props }) => (
              <blockquote style={styles.blockquote} {...props} />
            ),
          }}
        />
      </div>

      {/* ── PROJELERE GÖZ AT ── */}
      {similarProjects.length > 0 && (
        <div style={styles.projectsSection}>
          <div style={styles.projectsInner}>
            <div style={styles.projectsHeader}>
              <h3 style={styles.projectsTitle}>Projelere Göz At</h3>
              <div style={styles.titleUnderline} />
            </div>
            <div style={styles.similarGrid}>
              {similarProjects.map((project) => (
                <div
                  key={project.id}
                  style={styles.similarCard}
                  onClick={() => navigate(`/project/${project.id}`)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-5px)";
                    e.currentTarget.style.boxShadow =
                      "0 12px 28px rgba(0,0,0,0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 2px 10px rgba(0,0,0,0.08)";
                  }}
                >
                  <div style={styles.cardImageWrapper}>
                    <img
                      src={
                        project.image_urls?.[0] ||
                        "https://via.placeholder.com/400x220?text=Görsel+Yok"
                      }
                      alt={project.project_name}
                      style={styles.cardImage}
                    />
                    {project.project_type && (
                      <span style={styles.cardBadge}>
                        {project.project_type}
                      </span>
                    )}
                  </div>
                  <div style={styles.cardBody}>
                    <h4 style={styles.cardTitle}>{project.project_name}</h4>
                    <div style={styles.cardMeta}>
                      <svg
                        style={styles.cardMetaIcon}
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                        <circle
                          cx="12"
                          cy="9"
                          r="2.5"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                      </svg>
                      <span style={styles.cardLocation}>
                        {[project.district, project.city]
                          .filter(Boolean)
                          .join(", ") || "Konum belirtilmemiş"}
                      </span>
                    </div>
                    <div style={styles.cardFooter}>
                      <span style={styles.cardPrice}>
                        {formatPrice(project.price)}
                      </span>
                      <span style={styles.cardCta}>İncele →</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  page: {
    backgroundColor: "#fff",
    margin: 0,
    padding: 0,
    fontFamily: "'Georgia', 'Times New Roman', serif",
  },

  /* ── HERO ── */
  hero: {
    position: "relative",
    width: "100%",
    height: "520px",
    overflow: "hidden",
    backgroundColor: "#111",
  },
  heroImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "center",
    display: "block",
    opacity: 0.72,
  },
  heroOverlay: {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.35) 55%, rgba(0,0,0,0.05) 100%)",
  },
  heroContent: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    padding: "40px 48px",
    maxWidth: "960px",
    margin: "0 auto",
  },
  categoryBadge: {
    display: "inline-block",
    backgroundColor: "#2a9d8f",
    color: "#fff",
    fontSize: "11px",
    fontWeight: "700",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    padding: "4px 12px",
    borderRadius: "3px",
    marginBottom: "14px",
    fontFamily: "'Segoe UI', Arial, sans-serif",
  },
  heroTitle: {
    fontSize: "clamp(26px, 4vw, 42px)",
    fontWeight: "700",
    color: "#ffffff",
    lineHeight: "1.25",
    margin: "0 0 18px 0",
    textShadow: "0 2px 8px rgba(0,0,0,0.4)",
    fontFamily: "'Georgia', serif",
  },
  hereMeta: {
    display: "flex",
    gap: "24px",
    flexWrap: "wrap",
  },
  metaItem: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "13px",
    color: "rgba(255,255,255,0.82)",
    fontFamily: "'Segoe UI', Arial, sans-serif",
  },
  metaIcon: {
    width: "14px",
    height: "14px",
    flexShrink: 0,
  },

  /* ── CONTENT ── */
  contentWrapper: {
    maxWidth: "760px",
    margin: "0 auto",
    padding: "48px 24px 64px",
  },
  leadText: {
    fontSize: "20px",
    lineHeight: "1.7",
    color: "#1a1a1a",
    fontWeight: "400",
    fontFamily: "'Georgia', serif",
    borderLeft: "4px solid #2a9d8f",
    paddingLeft: "20px",
    marginBottom: "32px",
    marginTop: 0,
  },
  divider: {
    width: "60px",
    height: "3px",
    backgroundColor: "#2a9d8f",
    marginBottom: "32px",
    borderRadius: "2px",
  },
  bodyText: {
    fontSize: "18px",
    lineHeight: "1.85",
    color: "#333",
    marginBottom: "24px",
    fontFamily: "'Georgia', serif",
  },
  inlineH2: {
    fontSize: "26px",
    fontWeight: "700",
    color: "#1a1a1a",
    marginTop: "40px",
    marginBottom: "16px",
    fontFamily: "'Georgia', serif",
  },
  inlineH3: {
    fontSize: "20px",
    fontWeight: "700",
    color: "#2c3e50",
    marginTop: "32px",
    marginBottom: "12px",
    fontFamily: "'Georgia', serif",
  },
  blockquote: {
    margin: "32px 0",
    padding: "20px 28px",
    backgroundColor: "#f7f9fb",
    borderLeft: "4px solid #2a9d8f",
    borderRadius: "0 6px 6px 0",
    fontSize: "19px",
    fontStyle: "italic",
    color: "#444",
    lineHeight: "1.7",
  },

  /* ── LOADING / ERROR ── */
  loading: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "60vh",
  },
  spinner: {
    width: "36px",
    height: "36px",
    border: "3px solid #e0e0e0",
    borderTop: "3px solid #2a9d8f",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
  error: {
    padding: "100px",
    textAlign: "center",
    fontSize: "1.4rem",
    color: "#c0392b",
  },

  /* ── PROJELER ── */
  projectsSection: {
    backgroundColor: "#f5f7f6",
    borderTop: "1px solid #e5e8e6",
    padding: "60px 24px",
  },
  projectsInner: {
    maxWidth: "1100px",
    margin: "0 auto",
  },
  projectsHeader: {
    marginBottom: "32px",
  },
  projectsTitle: {
    fontSize: "22px",
    fontWeight: "700",
    color: "#1a1a1a",
    margin: "0 0 10px 0",
  },
  titleUnderline: {
    width: "48px",
    height: "3px",
    backgroundColor: "#2a9d8f",
    borderRadius: "2px",
  },
  similarGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "24px",
  },
  similarCard: {
    backgroundColor: "#fff",
    borderRadius: "10px",
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
    cursor: "pointer",
    transition: "transform 0.25s ease, box-shadow 0.25s ease",
  },
  cardImageWrapper: {
    position: "relative",
    width: "100%",
    height: "160px",
    overflow: "hidden",
    backgroundColor: "#e9ecef",
  },
  cardImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
    transition: "transform 0.3s ease",
  },
  cardBadge: {
    position: "absolute",
    top: "10px",
    left: "10px",
    backgroundColor: "rgba(42,157,143,0.92)",
    color: "#fff",
    fontSize: "10px",
    fontWeight: "700",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    padding: "3px 9px",
    borderRadius: "3px",
    fontFamily: "'Segoe UI', Arial, sans-serif",
  },
  cardBody: {
    padding: "16px",
  },
  cardTitle: {
    fontSize: "15px",
    fontWeight: "700",
    color: "#1a1a1a",
    margin: "0 0 10px 0",
    lineHeight: "1.4",
    fontFamily: "'Segoe UI', Arial, sans-serif",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  cardMeta: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    marginBottom: "14px",
  },
  cardMetaIcon: {
    width: "13px",
    height: "13px",
    color: "#2a9d8f",
    flexShrink: 0,
  },
  cardLocation: {
    fontSize: "12px",
    color: "#666",
    fontFamily: "'Segoe UI', Arial, sans-serif",
  },
  cardFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: "12px",
    borderTop: "1px solid #f0f0f0",
  },
  cardPrice: {
    fontSize: "14px",
    fontWeight: "700",
    color: "#2a9d8f",
    fontFamily: "'Segoe UI', Arial, sans-serif",
  },
  cardCta: {
    fontSize: "12px",
    color: "#888",
    fontFamily: "'Segoe UI', Arial, sans-serif",
    letterSpacing: "0.03em",
  },
};

/* Spinner animasyonu */
if (typeof document !== "undefined") {
  const style = document.createElement("style");
  style.textContent = `
    @keyframes spin { to { transform: rotate(360deg); } }
    @media (max-width: 600px) {
      .hero { height: 340px !important; }
      .heroContent { padding: 24px 20px !important; }
    }
  `;
  document.head.appendChild(style);
}

export default HaberDetay;